import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AuditTrailViewer } from '@/components/leave/audit/AuditTrailViewer';
import { useHasPermission, useHasAnyPermission } from '@/hooks/permissions';
import { useCompanyEmployees } from '@/hooks/leave';
import { PERMISSION_KEYS } from '@/types/roles';
import { History, Users, ShieldX } from 'lucide-react';

/**
 * History Management page with per-tab permission gates.
 *
 * Route guard (App.tsx): OR of all tab permissions — grants page access.
 * Each tab independently checks its own permission before rendering content/queries.
 *
 * Permission mapping per tab:
 *   - Leave Accrual:      approve_leave_requests | system_config_manage
 *   - Timesheet History:   approve_timesheets       (future)
 *   - Schedule Changes:    roster_manager | add_edit_shifts (future)
 */

const NoAccessFallback = ({ tabName }: { tabName: string }) => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center py-12">
      <ShieldX className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold mb-2">{tabName}</h3>
      <p className="text-muted-foreground text-center">
        You don't have permission to view this section. Contact your administrator to request access.
      </p>
    </CardContent>
  </Card>
);

const HistoryManagement = () => {
  const { t } = useTranslation();

  // Per-tab permission checks
  const canViewLeaveAccrual = useHasAnyPermission([
    PERMISSION_KEYS.APPROVE_LEAVE_REQUESTS,
    PERMISSION_KEYS.SYSTEM_CONFIG_MANAGE,
  ]);
  const canViewTimesheetHistory = useHasPermission(PERMISSION_KEYS.APPROVE_TIMESHEETS);
  const canViewScheduleChanges = useHasAnyPermission([
    PERMISSION_KEYS.ROSTER_MANAGER,
    PERMISSION_KEYS.ADD_EDIT_SHIFTS,
  ]);

  // Employee filter (only relevant for leave accrual tab when user has access)
  const canViewEmployees = useHasPermission(PERMISSION_KEYS.VIEW_EMPLOYEE_DETAILS);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('all');
  const { data: employees = [] } = useCompanyEmployees();

  // Determine the default tab: first accessible one
  const defaultTab = useMemo(() => {
    if (canViewLeaveAccrual) return 'leave-accrual';
    if (canViewTimesheetHistory) return 'future-1';
    if (canViewScheduleChanges) return 'future-2';
    return 'leave-accrual';
  }, [canViewLeaveAccrual, canViewTimesheetHistory, canViewScheduleChanges]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <History className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold">History Management</h1>
          <p className="text-muted-foreground">
            View and manage historical data and audit trails
          </p>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList>
          <TabsTrigger value="leave-accrual" disabled={!canViewLeaveAccrual}>
            Leave Accrual
          </TabsTrigger>
          <TabsTrigger value="future-1">
            Timesheet History
          </TabsTrigger>
          <TabsTrigger value="future-2">
            Schedule Changes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="leave-accrual" className="space-y-6">
          {canViewLeaveAccrual ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Leave Accrual Audit Trail
                </CardTitle>
                <CardDescription>
                  View comprehensive accrual history for compliance and auditing purposes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {canViewEmployees && (
                  <div className="grid gap-2">
                    <Label htmlFor="employee-select">Employee Filter</Label>
                    <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                      <SelectTrigger>
                        <SelectValue placeholder="All employees" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All employees</SelectItem>
                        {employees.map((employee) => (
                          <SelectItem key={employee.id} value={employee.id}>
                            {employee.first_name} {employee.last_name} ({employee.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <AuditTrailViewer
                  profileId={selectedEmployeeId === 'all' ? undefined : selectedEmployeeId}
                  className="mt-6"
                />
              </CardContent>
            </Card>
          ) : (
            <NoAccessFallback tabName="Leave Accrual" />
          )}
        </TabsContent>

        <TabsContent value="future-1" className="space-y-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <History className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Timesheet History</h3>
              <p className="text-muted-foreground text-center">
                This is a placeholder for future implementation.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="future-2" className="space-y-6">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <History className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Schedule Changes</h3>
              <p className="text-muted-foreground text-center">
                This is a placeholder for future implementation.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default HistoryManagement;
